import React from 'react';
import PropTypes from 'prop-types';
import { Container } from 'semantic-ui-react';
import TeaserImages from '../Helpers/TeaserImages';
import RelatedItemsDisplay from '../Helpers/RelatedItemsDisplay';
import Sections from '../Helpers/Sections';
import DefaultViewWithoutTitle from '../../Custom/DefaultViewWithoutTitle';
import {
  RESEARCHER_POSITIONS,
  STUDENT_ASSISTANT_POSITIONS,
  filterByPosition,
} from '../Helpers/positionFilters';
import { hasImage, normalizePerson } from '../../../helpers/content';
import { flattenRichTextToAppURL } from '../../../helpers/richText';
import { formatDate } from '../../../helpers/dates';

const ProjectView = (props) => {
  const { content } = props;
  const teaserImage1 = content.teaser_image1;
  const teaserImage2 = content.teaser_image2;

  const relatedMembers = Array.isArray(content.related_members)
    ? content.related_members.map(normalizePerson).filter(Boolean)
    : [];
  const displayMembers = filterByPosition(relatedMembers, RESEARCHER_POSITIONS);

  const hasImages = hasImage(teaserImage1) || hasImage(teaserImage2);
  const hasMetaLeft =
    content.project_funding ||
    content.funder_name ||
    content.grant_id ||
    content.funding_program;
  const hasMetaRight =
    content.project_start_date ||
    content.project_end_date ||
    content.funding_period;
  const hasMeta = hasMetaLeft || hasMetaRight;
  const hasAbstract =
    content.project_abstract?.data &&
    content.project_abstract.data !== '<p></p>';
  const hasLinks = content.project_website || content.funding_link;

  const hasMembers = displayMembers.length > 0 && !content.hide_related_members;

  const assistants = filterByPosition(
    content.related_assistants,
    STUDENT_ASSISTANT_POSITIONS,
  );
  const hasAssistants =
    assistants.length > 0 && !content.hide_related_assistants;

  const hasSponsors =
    content.related_sponsors?.length > 0 && !content.hide_related_sponsors;

  const hasTopics =
    content.related_topics?.length > 0 && !content.hide_related_topics;

  // The blocks - Section Tabs included - stay in their own order and are
  // rendered after everything the fields produce. Hoisting the tab bar up
  // under the header put a sub-page navigation above the project's own
  // funding, dates and people, which reads as if the tabs belonged to the
  // header rather than to the page below them.
  const blocksLayoutItems = content?.blocks_layout?.items || [];
  const allBlocks = content?.blocks || {};
  const hasBlocks = blocksLayoutItems.some((id) => {
    const block = allBlocks[id];
    if (!block) return false;
    if (block['@type'] !== 'slate') return true;
    return (block.plaintext || '').trim().length > 0;
  });

  const hasFieldSections =
    hasImages ||
    hasMeta ||
    hasAbstract ||
    hasLinks ||
    hasMembers ||
    hasAssistants ||
    hasSponsors ||
    hasTopics;

  return (
    <Container id="view-wrapper" className="ProjectView">
      {/* Header */}
      <div className="view-header">
        <h2 className="view-header__title">{content.title || content.id}</h2>
        {/* No description here. `description` is a listing field - the one
            line that goes on a card and into a search result - and repeating
            it as the first paragraph of the page it summarises says the same
            thing twice. The full view has its own content. */}
        <div className="view-header__divider" />
      </div>
      <Sections>
        {/* Teaser Images */}
        {hasImages && <TeaserImages content={content} />}

        {/* 2-Column Metadata */}
        {hasMeta && (
          <>
            <div className="view-meta-grid">
              <div className="view-meta-grid__col">
                {content.project_funding && (
                  <div className="view-section">
                    <h3 className="view-section__label">Funding:</h3>
                    <p className="view-section__value">
                      {content.project_funding}
                    </p>
                  </div>
                )}
                {content.funder_name && (
                  <div className="view-section">
                    <h3 className="view-section__label">Funder:</h3>
                    <p className="view-section__value">{content.funder_name}</p>
                  </div>
                )}
                {content.grant_id && (
                  <div className="view-section">
                    <h3 className="view-section__label">Grant ID:</h3>
                    <p className="view-section__value">{content.grant_id}</p>
                  </div>
                )}
                {content.funding_program && (
                  <div className="view-section">
                    <h3 className="view-section__label">Funding Program:</h3>
                    <p className="view-section__value">
                      {content.funding_program}
                    </p>
                  </div>
                )}
              </div>
              <div className="view-meta-grid__col">
                {content.project_start_date && (
                  <div className="view-section">
                    <h3 className="view-section__label">Start:</h3>
                    <p className="view-section__value">
                      {formatDate(content.project_start_date)}
                    </p>
                  </div>
                )}
                {content.project_end_date && (
                  <div className="view-section">
                    <h3 className="view-section__label">End:</h3>
                    <p className="view-section__value">
                      {formatDate(content.project_end_date)}
                    </p>
                  </div>
                )}
                {content.funding_period && (
                  <div className="view-section">
                    <h3 className="view-section__label">Funding Period:</h3>
                    <p className="view-section__value">
                      {content.funding_period}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Abstract */}
        {hasAbstract && (
          <>
            <div className="view-section">
              <h3 className="view-section__label">Project Abstract:</h3>
              <div
                className="view-section__richtext"
                dangerouslySetInnerHTML={{
                  __html: flattenRichTextToAppURL(
                    content.project_abstract.data,
                  ),
                }}
              />
            </div>
          </>
        )}

        {/* Links */}
        {hasLinks && (
          <>
            <div className="view-section">
              <h3 className="view-section__label">Links:</h3>
              <div className="view-section__buttons">
                {content.project_website && (
                  <a
                    href={content.project_website}
                    className="view-section__button"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Project Website
                  </a>
                )}
                {content.funding_link && (
                  <a
                    href={content.funding_link}
                    className="view-section__button"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Funding Link
                  </a>
                )}
              </div>
            </div>
          </>
        )}

        {/* Project Members */}
        {hasMembers && (
          <>
            <div className="view-section">
              <h3 className="view-section__label">Project Members:</h3>
              <RelatedItemsDisplay
                items={displayMembers}
                mode={content.related_members_display}
                kind="person"
                fieldKey={`${content['@id']}-related_members`}
              />
            </div>
          </>
        )}

        {/* Student Assistants */}
        {hasAssistants && (
          <>
            <div className="view-section">
              <h3 className="view-section__label">Student Assistants:</h3>
              <RelatedItemsDisplay
                items={assistants}
                mode={content.related_assistants_display}
                kind="person"
                fieldKey={`${content['@id']}-related_assistants`}
              />
            </div>
          </>
        )}

        {/* Sponsors */}
        {hasSponsors && (
          <>
            <div className="view-section">
              <h3 className="view-section__label">Sponsors / Funders:</h3>
              <RelatedItemsDisplay
                items={content.related_sponsors}
                mode={content.related_sponsors_display}
                kind="plain"
                fieldKey={`${content['@id']}-related_sponsors`}
              />
            </div>
          </>
        )}

        {/* Related Topics */}
        {hasTopics && (
          <>
            <div className="view-section">
              <h3 className="view-section__label">Related Topics:</h3>
              <RelatedItemsDisplay
                items={content.related_topics}
                mode={content.related_topics_display}
                kind="plain"
                fieldKey={`${content['@id']}-related_topics`}
              />
            </div>
          </>
        )}
      </Sections>

      {/* The block-rendered part of the page */}
      {hasBlocks && hasFieldSections && <div className="view-divider" />}
      <DefaultViewWithoutTitle {...props} content={content} />
    </Container>
  );
};

ProjectView.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    id: PropTypes.string,
    description: PropTypes.string,
    project_abstract: PropTypes.shape({ data: PropTypes.string }),
    teaser_image1: PropTypes.object,
    teaser_image1_caption: PropTypes.string,
    teaser_image1_copyright: PropTypes.string,
    teaser_image1_copyright_black_font: PropTypes.bool,
    teaser_image2: PropTypes.object,
    teaser_image2_caption: PropTypes.string,
    teaser_image2_copyright: PropTypes.string,
    teaser_image2_copyright_black_font: PropTypes.bool,
    project_website: PropTypes.string,
    project_start_date: PropTypes.string,
    project_end_date: PropTypes.string,
    project_funding: PropTypes.string,
    funder_name: PropTypes.string,
    grant_id: PropTypes.string,
    funding_program: PropTypes.string,
    funding_period: PropTypes.string,
    funding_link: PropTypes.string,
    hide_related_members: PropTypes.bool,
    hide_related_assistants: PropTypes.bool,
    hide_related_sponsors: PropTypes.bool,
    hide_related_topics: PropTypes.bool,
    related_members_display: PropTypes.string,
    related_assistants_display: PropTypes.string,
    related_sponsors_display: PropTypes.string,
    related_topics_display: PropTypes.string,
    related_members: PropTypes.arrayOf(
      PropTypes.shape({
        '@id': PropTypes.string,
        '@type': PropTypes.string,
        review_state: PropTypes.string,
        full_name: PropTypes.string,
        title: PropTypes.string,
        type_of_position: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.shape({ title: PropTypes.string, token: PropTypes.string }),
        ]),
      }),
    ),
    related_assistants: PropTypes.arrayOf(
      PropTypes.shape({
        '@id': PropTypes.string,
        '@type': PropTypes.string,
        review_state: PropTypes.string,
        full_name: PropTypes.string,
        title: PropTypes.string,
        type_of_position: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.shape({ title: PropTypes.string, token: PropTypes.string }),
        ]),
      }),
    ),
    related_sponsors: PropTypes.arrayOf(
      PropTypes.shape({ '@id': PropTypes.string, title: PropTypes.string }),
    ),
    related_topics: PropTypes.arrayOf(
      PropTypes.shape({ '@id': PropTypes.string, title: PropTypes.string }),
    ),
  }).isRequired,
};

export default ProjectView;
